import React, { useRef, useEffect, useState } from 'react';
import { Snackbar, Box, Typography, IconButton } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface NotificationProps {
  open: boolean;
  message: string;
  type: NotificationType;
  onClose: () => void;
  linkUrl?: string; // Opcjonalny URL do linku w powiadomieniu
  title?: string; // Opcjonalny tytuł powiadomienia
}

/**
 * Komponent powiadomień używany w całej aplikacji.
 * Wyświetla powiadomienia w górnej części ekranu, na środku topbara.
 * Zgodny z designem z Figmy.
 */
const Notification: React.FC<NotificationProps> = ({ open, message, type, onClose, linkUrl, title }) => {
  const messageRef = useRef<HTMLDivElement>(null);
  const [showCopyButton, setShowCopyButton] = useState(false);

  // Sprawdź czy tekst jest dłuższy niż 2 linie
  useEffect(() => {
    if (messageRef.current && open) {
      const element = messageRef.current;
      // Sprawdź czy wysokość elementu przekracza 2 linie (16px line-height * 2 = 32px)
      // Dodajemy mały margines błędu (np. 36px) aby uwzględnić padding i zaokrąglenia
      const isOverflowing = element.scrollHeight > 36;
      setShowCopyButton(isOverflowing);
    } else {
      setShowCopyButton(false);
    }
  }, [message, open]);

  // Funkcja kopiowania do schowka
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(message);
      // Opcjonalnie: pokaż krótkie potwierdzenie kopiowania
    } catch (err) {
      console.error('Nie udało się skopiować tekstu:', err);
    }
  };

  // Mapowanie typów powiadomień na kolory i ikony
  const getNotificationConfig = (type: NotificationType) => {
    switch (type) {
      case 'success':
        return {
          backgroundColor: '#CCE9D1', // rgb(0.8, 0.9137, 0.8196) z Figmy
          iconColor: '#007412', // rgb(0, 0.4555, 0.0759) z Figmy
          textColor: '#2D2D32', // rgb(0.176, 0.176, 0.197) z Figmy
          titleColor: '#007412',
          icon: <CheckCircleIcon sx={{ width: 24, height: 24, color: '#007412' }} />
        };
      case 'warning':
        return {
          backgroundColor: '#F1E2CC',
          iconColor: '#985B00',
          textColor: '#2D2D32',
          titleColor: '#985B00',
          icon: (
            <Box
              component="svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              sx={{ width: 24, height: 24 }}
            >
              <path
                d="M3.42558 20.4998C3.25608 20.4998 3.10383 20.4584 2.96883 20.3756C2.83383 20.2927 2.72891 20.1835 2.65408 20.0478C2.57608 19.9131 2.53316 19.7673 2.52533 19.6103C2.5175 19.4533 2.55983 19.2978 2.65233 19.1438L11.2131 4.35581C11.3057 4.20181 11.4216 4.08798 11.5606 4.01431C11.6997 3.94064 11.8462 3.90381 12.0001 3.90381C12.1539 3.90381 12.3004 3.94064 12.4396 4.01431C12.5786 4.08798 12.6944 4.20181 12.7871 4.35581L21.3478 19.1438C21.4403 19.2978 21.4827 19.4533 21.4748 19.6103C21.467 19.7673 21.4241 19.9131 21.3461 20.0478C21.2712 20.1835 21.1663 20.2927 21.0313 20.3756C20.8963 20.4584 20.7441 20.4998 20.5746 20.4998H3.42558ZM12.0001 17.8076C12.2289 17.8076 12.4207 17.7301 12.5756 17.5753C12.7304 17.4205 12.8078 17.2286 12.8078 16.9998C12.8078 16.771 12.7304 16.5791 12.5756 16.4243C12.4207 16.2695 12.2289 16.1921 12.0001 16.1921C11.7712 16.1921 11.5794 16.2695 11.4246 16.4243C11.2697 16.5791 11.1923 16.771 11.1923 16.9998C11.1923 17.2286 11.2697 17.4205 11.4246 17.5753C11.5794 17.7301 11.7712 17.8076 12.0001 17.8076ZM12.0003 15.1921C12.213 15.1921 12.3911 15.1202 12.5346 14.9766C12.6782 14.8327 12.7501 14.6546 12.7501 14.4421V10.9421C12.7501 10.7296 12.6782 10.5515 12.5343 10.4078C12.3905 10.264 12.2123 10.1921 11.9998 10.1921C11.7872 10.1921 11.6091 10.264 11.4656 10.4078C11.3219 10.5515 11.2501 10.7296 11.2501 10.9421V14.4421C11.2501 14.6546 11.322 14.8327 11.4658 14.9766C11.6097 15.1202 11.7878 15.1921 12.0003 15.1921Z"
                fill="#985B00"
              />
            </Box>
          )
        };
      case 'error':
        return {
          backgroundColor: '#F2CCCD',
          iconColor: '#7C0002',
          textColor: '#2D2D32',
          titleColor: '#7C0002',
          icon: (
            <Box
              component="svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              sx={{ width: 24, height: 24 }}
            >
              <path
                d="M12 16.7308C12.2288 16.7308 12.4207 16.6533 12.5755 16.4985C12.7303 16.3437 12.8077 16.1518 12.8077 15.923C12.8077 15.6942 12.7303 15.5023 12.5755 15.3475C12.4207 15.1928 12.2288 15.1155 12 15.1155C11.7712 15.1155 11.5793 15.1928 11.4245 15.3475C11.2697 15.5023 11.1923 15.6942 11.1923 15.923C11.1923 16.1518 11.2697 16.3437 11.4245 16.4985C11.5793 16.6533 11.7712 16.7308 12 16.7308ZM12.0003 13.077C12.2129 13.077 12.391 13.0051 12.5345 12.8613C12.6782 12.7176 12.75 12.5395 12.75 12.327V7.827C12.75 7.6145 12.6781 7.43633 12.5343 7.2925C12.3904 7.14883 12.2122 7.077 11.9998 7.077C11.7871 7.077 11.609 7.14883 11.4655 7.2925C11.3218 7.43633 11.25 7.6145 11.25 7.827V12.327C11.25 12.5395 11.3219 12.7176 11.4658 12.8613C11.6096 13.0051 11.7878 13.077 12.0003 13.077ZM12.0017 21.5C10.6877 21.5 9.45267 21.2507 8.2965 20.752C7.14033 20.2533 6.13467 19.5766 5.2795 18.7218C4.42433 17.8669 3.74725 16.8617 3.24825 15.706C2.74942 14.5503 2.5 13.3156 2.5 12.0017C2.5 10.6877 2.74933 9.45267 3.248 8.2965C3.74667 7.14033 4.42342 6.13467 5.27825 5.2795C6.13308 4.42433 7.13833 3.74725 8.294 3.24825C9.44967 2.74942 10.6844 2.5 11.9983 2.5C13.3123 2.5 14.5473 2.74933 15.7035 3.248C16.8597 3.74667 17.8653 4.42342 18.7205 5.27825C19.5757 6.13308 20.2528 7.13833 20.7518 8.294C21.2506 9.44967 21.5 10.6844 21.5 11.9983C21.5 13.3123 21.2507 14.5473 20.752 15.7035C20.2533 16.8597 19.5766 17.8653 18.7218 18.7205C17.8669 19.5757 16.8617 20.2528 15.706 20.7518C14.5503 21.2506 13.3156 21.5 12.0017 21.5Z"
                fill="#7C0002"
              />
            </Box>
          )
        };
      case 'info':
        return {
          backgroundColor: '#E6F0FF', // Kolor z chipa "w trakcie"
          iconColor: '#1976D2', // Ciemniejszy niebieski
          textColor: '#2D2D32',
          titleColor: '#1976D2',
          icon: (
            <Box
              component="svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              sx={{ width: 24, height: 24 }}
            >
              <path
                d="M12.0003 16.75C12.2129 16.75 12.391 16.6781 12.5345 16.5343C12.6782 16.3906 12.75 16.2125 12.75 16V11.75C12.75 11.5375 12.6781 11.3593 12.5343 11.2155C12.3904 11.0718 12.2122 11 11.9998 11C11.7871 11 11.609 11.0718 11.4655 11.2155C11.3218 11.3593 11.25 11.5375 11.25 11.75V16C11.25 16.2125 11.3219 16.3906 11.4658 16.5343C11.6096 16.6781 11.7878 16.75 12.0003 16.75ZM12 9.2885C12.2288 9.2885 12.4207 9.21108 12.5755 9.05625C12.7303 8.90142 12.8077 8.70958 12.8077 8.48075C12.8077 8.25192 12.7303 8.06008 12.5755 7.90525C12.4207 7.75058 12.2288 7.67325 12 7.67325C11.7712 7.67325 11.5793 7.75058 11.4245 7.90525C11.2697 8.06008 11.1923 8.25192 11.1923 8.48075C11.1923 8.70958 11.2697 8.90142 11.4245 9.05625C11.5793 9.21108 11.7712 9.2885 12 9.2885ZM12.0017 21.5C10.6877 21.5 9.45267 21.2507 8.2965 20.752C7.14033 20.2533 6.13467 19.5766 5.2795 18.7218C4.42433 17.8669 3.74725 16.8617 3.24825 15.706C2.74942 14.5503 2.5 13.3156 2.5 12.0017C2.5 10.6877 2.74933 9.45267 3.248 8.2965C3.74667 7.14033 4.42342 6.13467 5.27825 5.2795C6.13308 4.42433 7.13833 3.74725 8.294 3.24825C9.44967 2.74942 10.6844 2.5 11.9983 2.5C13.3123 2.5 14.5473 2.74933 15.7035 3.248C16.8597 3.74667 17.8653 4.42342 18.7205 5.27825C19.5757 6.13308 20.2528 7.13833 20.7518 8.294C21.2506 9.44967 21.5 10.6844 21.5 11.9983C21.5 13.3123 21.2507 14.5473 20.752 15.7035C20.2533 16.8597 19.5766 17.8653 18.7218 18.7205C17.8669 19.5757 16.8617 20.2528 15.706 20.7518C14.5503 21.2506 13.3156 21.5 12.0017 21.5Z"
                fill="#1976D2"
              />
            </Box>
          )
        };
      default:
        return {
          backgroundColor: '#E6F0FF',
          iconColor: '#1976D2',
          textColor: '#2D2D32',
          titleColor: '#1976D2',
          icon: (
            <Box
              component="svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              sx={{ width: 24, height: 24 }}
            >
              <path
                d="M12.0003 16.75C12.2129 16.75 12.391 16.6781 12.5345 16.5343C12.6782 16.3906 12.75 16.2125 12.75 16V11.75C12.75 11.5375 12.6781 11.3593 12.5343 11.2155C12.3904 11.0718 12.2122 11 11.9998 11C11.7871 11 11.609 11.0718 11.4655 11.2155C11.3218 11.3593 11.25 11.5375 11.25 11.75V16C11.25 16.2125 11.3219 16.3906 11.4658 16.5343C11.6096 16.6781 11.7878 16.75 12.0003 16.75ZM12 9.2885C12.2288 9.2885 12.4207 9.21108 12.5755 9.05625C12.7303 8.90142 12.8077 8.70958 12.8077 8.48075C12.8077 8.25192 12.7303 8.06008 12.5755 7.90525C12.4207 7.75058 12.2288 7.67325 12 7.67325C11.7712 7.67325 11.5793 7.75058 11.4245 7.90525C11.2697 8.06008 11.1923 8.25192 11.1923 8.48075C11.1923 8.70958 11.2697 8.90142 11.4245 9.05625C11.5793 9.21108 11.7712 9.2885 12 9.2885ZM12.0017 21.5C10.6877 21.5 9.45267 21.2507 8.2965 20.752C7.14033 20.2533 6.13467 19.5766 5.2795 18.7218C4.42433 17.8669 3.74725 16.8617 3.24825 15.706C2.74942 14.5503 2.5 13.3156 2.5 12.0017C2.5 10.6877 2.74933 9.45267 3.248 8.2965C3.74667 7.14033 4.42342 6.13467 5.27825 5.2795C6.13308 4.42433 7.13833 3.74725 8.294 3.24825C9.44967 2.74942 10.6844 2.5 11.9983 2.5C13.3123 2.5 14.5473 2.74933 15.7035 3.248C16.8597 3.74667 17.8653 4.42342 18.7205 5.27825C19.5757 6.13308 20.2528 7.13833 20.7518 8.294C21.2506 9.44967 21.5 10.6844 21.5 11.9983C21.5 13.3123 21.2507 14.5473 20.752 15.7035C20.2533 16.8597 19.5766 17.8653 18.7218 18.7205C17.8669 19.5757 16.8617 20.2528 15.706 20.7518C14.5503 21.2506 13.3156 21.5 12.0017 21.5Z"
                fill="#1976D2"
              />
            </Box>
          )
        };
    }
  };

  const config = getNotificationConfig(type);

  return (
    <Snackbar
      open={open}
        autoHideDuration={type === 'error' ? 10000 : 6000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{
        position: 'fixed',
        top: { xs: '62px', sm: '86px' }, // Podniesione o 46px w górę względem przycisku
        zIndex: 2000,
        width: '100%',
        '& .MuiPaper-root': {
          maxWidth: 'none',
          margin: 0,
          boxShadow: 'none',
          right: { xs: '35px', sm: '35px' } // Przesunięte w prawo o 1px
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: '8px',
          padding: '12px 16px',
          backgroundColor: config.backgroundColor,
          borderRadius: '4px',
          border: 'none',
          height: type === 'error' ? 'auto' : '73px',
          minHeight: '73px',
          width: type === 'error' ? '400px' : '218px',
          minWidth: type === 'error' ? '400px' : '218px',
          maxWidth: type === 'error' ? '500px' : '218px',
          boxShadow: 'none'
        }}
      >
        {/* Ikona */}
        <Box sx={{ flexShrink: 0, mt: 0.5 }}>
          {config.icon}
        </Box>

        {/* Treść powiadomienia */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '9px' }}>
          {title && (
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '16px',
                color: config.titleColor,
                margin: 0
              }}
            >
              {title}
            </Typography>
          )}
          <Typography
            ref={messageRef}
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '16px',
              color: config.textColor,
              margin: 0,
              display: type === 'error' ? 'block' : '-webkit-box',
              WebkitLineClamp: type === 'error' ? 10 : 2,
              WebkitBoxOrient: 'vertical',
              overflow: type === 'error' ? 'visible' : 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
              minWidth: 0,
              wordBreak: type === 'error' ? 'break-word' : 'normal',
              whiteSpace: type === 'error' ? 'normal' : 'normal'
            }}
          >
            {message}
            {linkUrl && (
              <Box
                component="a"
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: config.textColor,
                  textDecoration: 'underline',
                  marginLeft: '8px',
                  fontWeight: 500,
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
              >
                przejdź
              </Box>
            )}
          </Typography>
        </Box>

        {/* Przyciski po prawej stronie: zamknięcie i kopiuj */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', flexShrink: 0, width: 24, minWidth: 24, maxWidth: 24, height: '100%', position: 'relative' }}>
          {/* Przycisk zamknięcia */}
          <Box
            onClick={onClose}
            sx={{
              cursor: 'pointer',
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '&:hover': {
                opacity: 0.7
              }
            }}
          >
            <Box
              component="svg"
              width="11"
              height="11"
              viewBox="0 0 13 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              sx={{ width: 11, height: 11 }}
            >
              <path
                d="M6.34425 7.398L1.27125 12.4713C1.13275 12.6096 0.958667 12.6804 0.749 12.6838C0.5395 12.6869 0.36225 12.6161 0.21725 12.4713C0.0724167 12.3263 0 12.1506 0 11.9443C0 11.7379 0.0724167 11.5623 0.21725 11.4173L5.2905 6.34425L0.21725 1.27125C0.0789167 1.13275 0.00808339 0.958667 0.00475006 0.749001C0.00158339 0.539501 0.0724167 0.36225 0.21725 0.21725C0.36225 0.0724167 0.537917 0 0.74425 0C0.950583 0 1.12625 0.0724167 1.27125 0.21725L6.34425 5.2905L11.4173 0.21725C11.5557 0.0789167 11.7298 0.00808339 11.9395 0.00475006C12.149 0.00158339 12.3263 0.0724167 12.4713 0.21725C12.6161 0.36225 12.6885 0.537917 12.6885 0.74425C12.6885 0.950584 12.6161 1.12625 12.4713 1.27125L7.398 6.34425L12.4713 11.4173C12.6096 11.5558 12.6804 11.7298 12.6838 11.9395C12.6869 12.149 12.6161 12.3263 12.4713 12.4713C12.3263 12.6161 12.1506 12.6885 11.9443 12.6885C11.7379 12.6885 11.5623 12.6161 11.4173 12.4713L6.34425 7.398Z"
                fill={config.textColor}
                strokeWidth="0.5"
              />
            </Box>
          </Box>
          {/* Przycisk kopiuj - zawsze rezerwuj miejsce, nawet gdy niewidoczny */}
          <Box
            sx={{
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              visibility: showCopyButton ? 'visible' : 'hidden'
            }}
          >
            {showCopyButton && (
              <IconButton
                onClick={handleCopy}
                size="small"
                sx={{
                  width: 20,
                  height: 20,
                  padding: 0,
                  '&:hover': {
                    opacity: 0.7
                  }
                }}
                title="Kopiuj tekst"
              >
                <Box
                  component="svg"
                  width="16"
                  height="16"
                  viewBox="0 -960 960 960"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  sx={{ width: 16, height: 16 }}
                >
                  <path
                    d="M362.31-260Q332-260 311-281q-21-21-21-51.31v-455.38Q290-818 311-839q21-21 51.31-21h335.38Q728-860 749-839q21 21 21 51.31v455.38Q770-302 749-281q-21 21-51.31 21H362.31Zm0-60h335.38q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-455.38q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H362.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v455.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85Zm-140 200Q192-120 171-141q-21-21-21-51.31v-485.38q0-12.77 8.62-21.39 8.61-8.61 21.38-8.61t21.39 8.61q8.61 8.62 8.61 21.39v485.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85h365.38q12.77 0 21.39 8.61 8.61 8.62 8.61 21.39 0 12.77-8.61 21.38-8.62 8.62-21.39 8.62H222.31ZM350-320v-480 480Z"
                    fill={config.textColor}
                  />
                </Box>
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
    </Snackbar>
  );
};

export default Notification;
